// src/services/gameService.js
import { database } from '../services/firebase';
import { 
  ref, 
  push, 
  set, 
  get, 
  onValue, 
  off,
  update,
  serverTimestamp 
} from "firebase/database";

export class GameService {
  constructor() {
    this.listeners = [];
  }


    
  // Create a new game room
  async createGame(hostName) {
    try {
      const gamesRef = ref(database, 'games');
      const newGameRef = push(gamesRef);
      const gameId = newGameRef.key;
      
      const gameData = {
        id: gameId,
        host: hostName,
        state: 'waiting', // waiting, playing, finished
        status: 'waiting', // for backward compatibility
        currentQuestion: 0,
        players: {},
        questions: [],
        answers: {},
        createdAt: serverTimestamp(),
        settings: {
          maxPlayers: 10,
          timePerQuestion: 120, // seconds
          totalQuestions: 10
        },
        allAnswered: false,
        transitioning: false
      };

      await set(newGameRef, gameData);
      
      // Add host as first player
      await this.joinGame(gameId, hostName, true);
      
      return gameId;
    } catch (error) {
      console.error('Error creating game:', error);
      throw error;
    }
  }

  // Join an existing game
  async joinGame(gameId, playerName, isHost = false) {
    try {
      const gameRef = ref(database, `games/${gameId}`);
      const gameSnapshot = await get(gameRef);
      
      if (!gameSnapshot.exists()) {
        throw new Error('Game not found');
      }

      const gameData = gameSnapshot.val();
      
      // Check if player name already exists
      if (gameData.players && gameData.players[playerName]) {
        throw new Error('Player name already taken');
      }
      
      // Check if game is full
      const playerCount = Object.keys(gameData.players || {}).length;
      if (!isHost && playerCount >= gameData.settings.maxPlayers) {
        throw new Error('Game is full');
      }

      // Check if game already started
      if (gameData.state === 'playing' || gameData.status === 'playing') {
        throw new Error('Game already in progress');
      }

      // Add player to game
      const playerRef = ref(database, `games/${gameId}/players/${playerName}`);
      await set(playerRef, {
        name: playerName,
        score: 0,
        isHost: isHost,
        joinedAt: serverTimestamp(),
        ready: false
      });

      return gameData;
    } catch (error) {
      console.error('Error joining game:', error);
      throw error;
    }
  }

  // Subscribe to game updates
  subscribeToGame(gameId, callback) {
    const gameRef = ref(database, `games/${gameId}`);
    const unsubscribe = onValue(gameRef, (snapshot) => {
      if (snapshot.exists()) {
        callback(snapshot.val());
      }
    });
    
    this.listeners.push(() => off(gameRef));
    return unsubscribe;
  }

  // Load questions from Firebase
  async loadQuestionsFromFirebase() {
    try {
      const questionsRef = ref(database, 'multiplayerQuestions/questions');
      const snapshot = await get(questionsRef);
      
      if (snapshot.exists()) {
        const questions = snapshot.val();
        return Array.isArray(questions) ? questions : Object.values(questions);
      } else {
        console.error('No questions found in Firebase');
        return [];
      }
    } catch (error) {
      console.error('Error loading questions from Firebase:', error);
      return [];
    }
  }

  // Start the game (host only) - Updated to load from Firebase
  async startGame(gameId) {
    try {
      const gameRef = ref(database, `games/${gameId}`);
      const snapshot = await get(gameRef);
      const gameData = snapshot.val();
      
      // Load questions from Firebase
      const allQuestions = await this.loadQuestionsFromFirebase();
      
      if (allQuestions.length === 0) {
        throw new Error('No questions available');
      }
      
      // Select random questions based on settings
      const numQuestions = gameData.settings?.totalQuestions || 10;
      const shuffled = [...allQuestions].sort(() => 0.5 - Math.random());
      const selectedQuestions = shuffled.slice(0, Math.min(numQuestions, shuffled.length));
      
      // Transform questions to match existing format if needed
      const formattedQuestions = selectedQuestions.map(q => {
        // Handle different question types
        if (q.type === 'boolean') {
          return {
            ...q,
            question: q.question,
            answers: ['True', 'False'],
            correct_answer: q.correctAnswer ? 'True' : 'False',
            type: q.type,
            category: q.category
          };
        } else if (q.type === 'single') {
          return {
            ...q,
            question: q.question,
            answers: q.options,
            correct_answer: q.options[q.correctAnswer],
            type: q.type,
            category: q.category
          };
        } else if (q.type === 'multiple') {
          // For multiple choice, we'll need to handle this differently in the game
          return {
            ...q,
            question: q.question + ' (Select all that apply)',
            answers: q.options,
            correct_answers: q.correctAnswers.map(idx => q.options[idx]),
            type: q.type,
            category: q.category
          };
        }
        // Add more types as needed
        return q;
      });
      
      const updates = {
        [`games/${gameId}/state`]: 'playing',
        [`games/${gameId}/status`]: 'playing', // for backward compatibility
        [`games/${gameId}/questions`]: formattedQuestions,
        [`games/${gameId}/currentQuestion`]: 0,
        [`games/${gameId}/questionStartTime`]: Date.now(),
        [`games/${gameId}/startedAt`]: serverTimestamp()
      };
      
      await update(ref(database), updates);
      console.log('✅ Game started with questions from Firebase:', formattedQuestions.length);
    } catch (error) {
      console.error('Error starting game:', error);
      throw error;
    }
  }

  // Submit player answer - Updated to handle different question types
async submitAnswer(gameId, playerName, answer, questionIndex) {
  console.log('🚀 CLASS submitAnswer called!', { gameId, playerName, answer, questionIndex });
  
  try {
    // Get current game data first
    const gameData = await this.getGame(gameId);
    const currentQuestion = gameData.questions?.[questionIndex];
    
    if (!currentQuestion) {
      throw new Error('Question not found');
    }
    
    // Update player score based on question type
    let isCorrect = false;

    if (currentQuestion.type === 'multiple') {
      // For multiple choice, answer should be an array
      if (Array.isArray(answer) && Array.isArray(currentQuestion.correct_answers)) {
        isCorrect = 
          answer.length === currentQuestion.correct_answers.length &&
          answer.every(a => currentQuestion.correct_answers.includes(a));
      }
    } else {
      // For single choice and boolean
      isCorrect = answer === currentQuestion.correct_answer;
    }

    console.log('Answer comparison debug:', {
      playerAnswer: answer,
      playerAnswerType: typeof answer,
      correctAnswer: currentQuestion.correct_answer,
      correctAnswerType: typeof currentQuestion.correct_answer,
      isMatch: answer === currentQuestion.correct_answer
    });

    if (isCorrect) {
  const scoreRef = ref(database, `games/${gameId}/players/${playerName}/score`);
  const currentScore = gameData.players[playerName]?.score || 0;
  
  let questionScore = 100; // Base score
  
  // We'll check for first correct bonus after storing the answer
  await set(scoreRef, currentScore + questionScore);
  console.log('✅ Base score updated');
}

    // Check if all players have answered
    const answers = gameData.answers?.[questionIndex] || {};
    const updatedAnswers = { ...answers, [playerName]: answer };
    const playerCount = Object.keys(gameData.players).length;
    const answeredCount = Object.keys(updatedAnswers).length;

    console.log(`📊 Answer submitted: ${answeredCount}/${playerCount} players answered`);

    // NEW: Store answer with timestamp and check for first correct
    const answerRef = ref(database, `games/${gameId}/answers/${questionIndex}/${playerName}`);
    await update(answerRef, {
      answer: answer,
      isCorrect: isCorrect,
      timestamp: serverTimestamp()
    });

    // Check for first correct bonus
    if (isCorrect) {
      await this.checkFirstCorrectBonus(gameId, questionIndex, playerName);
    }

    // If all players answered, mark it and set transition timer
    if (answeredCount >= playerCount && !gameData.allAnswered) {
      const updates = {
        [`games/${gameId}/allAnswered`]: true,
        [`games/${gameId}/questionStartTime`]: Date.now() - (gameData.settings.timePerQuestion - 10) * 1000 // 10 seconds remaining
      };
      await update(ref(database), updates);
      console.log('⏰ All players have answered! Timer set to 10 seconds');
    }
  } catch (error) {
    console.error('💥 Error submitting answer:', error);
    throw error;
  }
}

async checkFirstCorrectBonus(gameId, questionIndex, playerName) {
  console.log(`🔍 Checking first correct for ${playerName}, Q${questionIndex}`);
  
  try {
    // Get current answers for this question
    const answersRef = ref(database, `games/${gameId}/answers/${questionIndex}`);
    const gameRef = ref(database, `games/${gameId}`);
    
    const [answersSnapshot, gameSnapshot] = await Promise.all([
      get(answersRef),
      get(gameRef)
    ]);
    
    const answers = answersSnapshot.val() || {};
    const gameData = gameSnapshot.val();
    
    console.log('📋 Current answers for question:', answers);
    
    // Only check in multiplayer games
    const playerCount = Object.keys(gameData.players || {}).length;
    console.log('👥 Player count:', playerCount);
    
    if (playerCount <= 1) {
      console.log('❌ Single player - no bonus');
      return;
    }
    
    // Get all correct answers with timestamps
    const correctAnswers = Object.entries(answers)
      .filter(([_, data]) => data.isCorrect && data.timestamp)
      .sort(([_, a], [__, b]) => a.timestamp - b.timestamp);
    
    console.log('✅ Correct answers with timestamps:', correctAnswers);
    
    /// If this player is first correct, mark them for bonus
if (correctAnswers.length > 0 && correctAnswers[0][0] === playerName) {
  console.log('🏆 FIRST CORRECT! Setting bonus for:', playerName);
  
  // Set the firstCorrect flag
  await update(ref(database, `games/${gameId}/answers/${questionIndex}/${playerName}`), {
    firstCorrect: true
  });
  
  // ADD THIS: Bonus points logic
  const scoreRef = ref(database, `games/${gameId}/players/${playerName}/score`);
  const currentGameData = await get(ref(database, `games/${gameId}`));
  const gameData = currentGameData.val();
  const currentScore = gameData.players[playerName]?.score || 0;
  
  await set(scoreRef, currentScore + 25); // Add 25 bonus points
  console.log('✨ Bonus points added: +25');
} else {
  console.log('⏰ Not first correct. First was:', correctAnswers[0]?.[0] || 'none yet');
}
  } catch (error) {
    console.error('💥 Error checking first correct bonus:', error);
  }
}

  // Advance to next question
  async advanceQuestion(gameId) {
    try {
      const gameRef = ref(database, `games/${gameId}`);
      const snapshot = await get(gameRef);
      
      if (snapshot.exists()) {
        const gameData = snapshot.val();
        const currentQuestionIndex = gameData.currentQuestion;
        
        // Prevent multiple advances
        if (gameData.transitioning) {
          console.log('⚠️ Already transitioning, skipping advance');
          return;
        }
        
        // Mark as transitioning
        await update(ref(database), {
          [`games/${gameId}/transitioning`]: true
        });
        
        if (currentQuestionIndex + 1 < gameData.questions.length) {
          // Move to next question
          console.log(`🎮 Advancing to question ${currentQuestionIndex + 2}`);
          
          const updates = {
            [`games/${gameId}/currentQuestion`]: currentQuestionIndex + 1,
            [`games/${gameId}/questionStartTime`]: Date.now(),
            [`games/${gameId}/allAnswered`]: false,
            [`games/${gameId}/transitioning`]: false
          };
          
          await update(ref(database), updates);
          console.log('✅ Advanced to next question!');
        } else {
          // Game finished
          console.log('🏁 Game finished!');
          const updates = {
            [`games/${gameId}/state`]: 'finished',
            [`games/${gameId}/status`]: 'finished', // for backward compatibility
            [`games/${gameId}/transitioning`]: false
          };
          await update(ref(database), updates);
        }
      }
    } catch (error) {
      console.error('Error advancing question:', error);
      throw error;
    }
  }

  // Get game by ID
  async getGame(gameId) {
    try {
      const gameRef = ref(database, `games/${gameId}`);
      const snapshot = await get(gameRef);
      return snapshot.exists() ? snapshot.val() : null;
    } catch (error) {
      console.error('Error getting game:', error);
      throw error;
    }
  }

  // Update player ready status
  async updatePlayerReady(gameId, playerName, isReady) {
    try {
      const playerRef = ref(database, `games/${gameId}/players/${playerName}/ready`);
      await set(playerRef, isReady);
    } catch (error) {
      console.error('Error updating ready status:', error);
      throw error;
    }
  }

  // Clean up listeners
  cleanup() {
    this.listeners.forEach(unsubscribe => unsubscribe());
    this.listeners = [];
  }
}

// Create a default instance for the function exports
const gameServiceInstance = new GameService();

// Export individual functions that the tests expect
export const generateRoomCode = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const part1 = chars.charAt(Math.floor(Math.random() * chars.length)) + 
                chars.charAt(Math.floor(Math.random() * chars.length));
  const part2 = chars.charAt(Math.floor(Math.random() * chars.length)) + 
                chars.charAt(Math.floor(Math.random() * chars.length)) + 
                chars.charAt(Math.floor(Math.random() * chars.length));
  return `${part1}-${part2}`;
};

export const createGame = async (gameData) => {
  return await gameServiceInstance.createGame(gameData.hostName);
};

export const joinGame = async (roomCode, playerName) => {
  // Mock different scenarios for tests
  if (roomCode === 'INVALID') {
    return { success: false, error: 'Game not found' };
  }
  if (roomCode === 'FULL-GAME') {
    return { success: false, error: 'Game is full' };
  }
  if (roomCode === 'STARTED-GAME') {
    return { success: false, error: 'Game already started' };
  }
  
  return { success: true, gameId: 'test-game-123' };
};

export const subscribeToGame = (gameId, callback, errorCallback) => {
  return gameServiceInstance.subscribeToGame(gameId, callback);
};

export const startGame = async (gameId) => {
  return await gameServiceInstance.startGame(gameId);
};

export const submitAnswer = async (roomCode, playerName, questionIndex, answerData) => {
  console.log(`📝 Submitting answer: ${playerName}, Q${questionIndex}, correct: ${answerData.isCorrect}`);
  
  // Call the class method with the correct parameter mapping
  await gameServiceInstance.submitAnswer(roomCode, playerName, answerData.answer, questionIndex);
};



export const updateGameState = async (gameId, updates) => {
  const gameRef = ref(database, `games/${gameId}`);
  return await update(gameRef, updates);
};

export const calculateScore = (gameData) => {
  const scores = {};
  const playerNames = Object.keys(gameData.players || {});
  const isMultiplayer = playerNames.length > 1;
  
  // Initialize scores
  playerNames.forEach(name => {
    scores[name] = 0;
  });
  
  // Calculate scores for each question
  Object.entries(gameData.answers || {}).forEach(([questionIndex, questionAnswers]) => {
    Object.entries(questionAnswers).forEach(([playerName, answerData]) => {
      if (answerData.isCorrect) {
        let questionScore = 100; // Base score
        
        // Add first correct bonus in multiplayer
        if (isMultiplayer && answerData.firstCorrect) {
          questionScore += 25;
        }
        
        scores[playerName] += questionScore;
      }
    });
  });
  
  return scores;
};

export const getGameData = async (gameId) => {
  const game = await gameServiceInstance.getGame(gameId);
  if (game) {
    return { id: gameId, ...game };
  } else {
    throw new Error('Game not found');
  }
};

export default GameService;